import { useState, useEffect, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wrench, Calendar as CalendarIcon, Clock, CheckCircle2, ArrowLeft, MapPin, Loader2, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useZipAutofill } from "@/hooks/useZipAutofill";

const SLOTS = [
  { start: 9, end: 11, label: "9:00 AM – 11:00 AM" },
  { start: 11, end: 13, label: "11:00 AM – 1:00 PM" },
  { start: 13, end: 15, label: "1:00 PM – 3:00 PM" },
  { start: 15, end: 17, label: "3:00 PM – 5:00 PM" },
];

const APPLIANCE_TYPES = [
  { value: "refrigerator", label: "Refrigerator" },
  { value: "washer", label: "Washer" },
  { value: "dryer", label: "Dryer" },
  { value: "dishwasher", label: "Dishwasher" },
  { value: "stove", label: "Stove / Oven" },
  { value: "commercial", label: "Commercial Appliance" },
];

const MANUFACTURERS = [
  { value: "ge", label: "GE" },
  { value: "whirlpool", label: "Whirlpool" },
  { value: "samsung", label: "Samsung" },
  { value: "lg", label: "LG" },
  { value: "frigidaire", label: "Frigidaire" },
  { value: "maytag", label: "Maytag" },
  { value: "other", label: "Other" },
];

function formatDateYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function humanDate(d) {
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function Booking() {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zip_code: "",
    county: "",
    appliance_type: "",
    manufacturer: "",
    problem_description: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [smsConsent, setSmsConsent] = useState(false);

  // Service-area ZIP quick check (step 1)
  const [zipQuery, setZipQuery] = useState("");
  const [zipCheck, setZipCheck] = useState(null); // { ok, reason, city, state }
  const [zipChecking, setZipChecking] = useState(false);

  // Auto-fill city/state/county on Step-2 when the customer types a 5-digit ZIP
  useZipAutofill(
    form.zip_code,
    { city: form.city, state: form.state, county: form.county },
    (patch) => setForm((s) => ({ ...s, ...patch })),
  );

  // Date bounds: tomorrow to +14 days
  const { minDate, maxDate } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const min = new Date(today);
    min.setDate(min.getDate() + 1);
    const max = new Date(today);
    max.setDate(max.getDate() + 14);
    return { minDate: min, maxDate: max };
  }, []);

  useEffect(() => {
    if (!selectedDate) {
      setAvailability([]);
      return;
    }
    setLoadingSlots(true);
    setSelectedSlot(null);
    api
      .get(`/booking/availability?date=${formatDateYMD(selectedDate)}`)
      .then((res) => setAvailability(res.data.slots || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canContinueStep1 = selectedDate && selectedSlot !== null && zipCheck?.ok;

  // Debounced ZIP check
  useEffect(() => {
    const zc = zipQuery.trim();
    if (zc.length !== 5 || !/^\d{5}$/.test(zc)) {
      setZipCheck(null);
      return;
    }
    let cancelled = false;
    setZipChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/booking/service-area/check?zip=${zc}`);
        if (cancelled) return;
        setZipCheck(res.data);
        if (res.data.ok) {
          // auto-fill the form
          setForm((f) => ({
            ...f,
            zip_code: zc,
            city: f.city || res.data.city || "",
            state: f.state || (res.data.state === "Maryland" ? "MD" : res.data.state || ""),
          }));
        }
      } catch (e) {
        if (!cancelled) setZipCheck({ ok: false, reason: formatApiError(e) });
      } finally {
        if (!cancelled) setZipChecking(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
      setZipChecking(false);
    };
  }, [zipQuery]);

  const canSubmit =
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.phone.trim() &&
    form.appliance_type &&
    form.manufacturer &&
    form.problem_description.trim();

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/booking", {
        ...form,
        email: form.email.trim() || null,
        scheduled_date: formatDateYMD(selectedDate),
        slot_start: selectedSlot,
        sms_consent: smsConsent,
      });
      setConfirmation({
        code: res.data.confirmation,
        date: humanDate(selectedDate),
        slot: SLOTS.find((s) => s.start === selectedSlot)?.label,
      });
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-xl text-slate-900">
              Bell Appliance Repair
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">
              Schedule Your Repair
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Stepper */}
        {step < 3 && (
          <div className="flex items-center gap-2 mb-6" data-testid="booking-stepper">
            <StepDot n={1} active={step >= 1} label="Date & Time" />
            <div className="flex-1 h-px bg-slate-200" />
            <StepDot n={2} active={step >= 2} label="Your Details" />
          </div>
        )}

        {step === 1 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-2xl">Choose a date & time</CardTitle>
              <p className="text-sm text-slate-500">
                Pick any available slot within the next 14 days. Same-day bookings are not
                available.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Service-area ZIP quick check */}
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4 space-y-2" data-testid="service-area-check">
                <Label htmlFor="zip-check" className="flex items-center gap-2 text-slate-800">
                  <MapPin className="w-4 h-4 text-blue-700" /> First, do we cover your area?
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="zip-check"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="Enter 5-digit ZIP"
                    value={zipQuery}
                    onChange={(e) => setZipQuery(e.target.value.replace(/\D/g, "").slice(0, 5))}
                    className="max-w-[220px] bg-white"
                    data-testid="zip-check-input"
                  />
                  {zipChecking && (
                    <span className="text-sm text-slate-500 flex items-center gap-1" data-testid="zip-checking">
                      <Loader2 className="w-4 h-4 animate-spin" /> Checking…
                    </span>
                  )}
                  {!zipChecking && zipCheck?.ok && (
                    <span className="text-sm text-emerald-700 flex items-center gap-1" data-testid="zip-ok">
                      <CheckCircle2 className="w-4 h-4" /> We serve {zipCheck.city || "your area"}!
                    </span>
                  )}
                  {!zipChecking && zipCheck && !zipCheck.ok && zipQuery.length === 5 && (
                    <span className="text-sm text-red-700 flex items-center gap-1" data-testid="zip-fail">
                      <XCircle className="w-4 h-4" /> Out of area
                    </span>
                  )}
                </div>
                {!zipChecking && zipCheck && !zipCheck.ok && zipQuery.length === 5 && (
                  <p className="text-sm text-red-700" data-testid="zip-fail-reason">{zipCheck.reason}</p>
                )}
              </div>

              <div className={`grid md:grid-cols-2 gap-6 ${!zipCheck?.ok ? "opacity-50 pointer-events-none" : ""}`} data-testid="date-slot-block">
                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-blue-700" /> Select date
                  </Label>
                  <div className="border border-slate-200 rounded-md bg-white inline-block">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(d) => d < minDate || d > maxDate || d.getDay() === 0 || d.getDay() === 6}
                      fromDate={minDate}
                      toDate={maxDate}
                      data-testid="booking-calendar"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-700" /> Select time slot
                  </Label>
                  {!selectedDate && (
                    <p className="text-sm text-slate-500 mt-2">Pick a date first.</p>
                  )}
                  {selectedDate && loadingSlots && (
                    <p className="text-sm text-slate-500 mt-2">Loading slots…</p>
                  )}
                  {selectedDate && !loadingSlots && (
                    <div className="space-y-2" data-testid="slot-list">
                      {SLOTS.map((s) => {
                        const info = availability.find((a) => a.start === s.start);
                        const booked = info?.booked;
                        const selected = selectedSlot === s.start;
                        return (
                          <button
                            key={s.start}
                            type="button"
                            disabled={booked}
                            onClick={() => setSelectedSlot(s.start)}
                            data-testid={`slot-${s.start}`}
                            className={`w-full text-left px-4 py-3 rounded-md border transition-all ${
                              booked
                                ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                : selected
                                ? "border-blue-700 bg-blue-50 text-blue-900 ring-2 ring-blue-100"
                                : "border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50/40"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{s.label}</span>
                              {booked && (
                                <span className="text-xs uppercase tracking-wider text-slate-400">
                                  Booked
                                </span>
                              )}
                              {!booked && selected && (
                                <CheckCircle2 className="w-4 h-4 text-blue-700" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end">
                <Button
                  disabled={!canContinueStep1}
                  onClick={() => {
                    // Carry the verified ZIP + city/state into Step 2 so the customer doesn't retype it.
                    // (autofill hook will fetch county from the same ZIP once it lands in the form.)
                    setForm((s) => ({
                      ...s,
                      zip_code: s.zip_code || zipQuery.trim(),
                      city: s.city || zipCheck?.city || "",
                      state: s.state || zipCheck?.state || "",
                    }));
                    setStep(2);
                  }}
                  className="bg-blue-700 hover:bg-blue-800"
                  data-testid="continue-to-details"
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle className="font-heading text-2xl">Your details</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {humanDate(selectedDate)} · {SLOTS.find((s) => s.start === selectedSlot)?.label}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(1)}
                  data-testid="back-to-slot"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="First name *" testid="booking-first-name">
                  <Input
                    value={form.first_name}
                    onChange={(e) => setField("first_name", e.target.value)}
                    data-testid="booking-first-name"
                  />
                </Field>
                <Field label="Last name *" testid="booking-last-name">
                  <Input
                    value={form.last_name}
                    onChange={(e) => setField("last_name", e.target.value)}
                    data-testid="booking-last-name"
                  />
                </Field>
                <Field label="Phone *">
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    data-testid="booking-phone"
                  />
                </Field>
                <Field label="Email">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    data-testid="booking-email"
                  />
                </Field>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <Field label="Street address">
                    <Input
                      value={form.street}
                      onChange={(e) => setField("street", e.target.value)}
                      data-testid="booking-street"
                    />
                  </Field>
                </div>
                <Field label="City">
                  <Input
                    value={form.city}
                    onChange={(e) => setField("city", e.target.value)}
                    data-testid="booking-city"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="State">
                    <Select
                      value={form.state}
                      onValueChange={(v) => setField("state", v)}
                    >
                      <SelectTrigger data-testid="booking-state">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MD">Maryland</SelectItem>
                        <SelectItem value="VA">Virginia</SelectItem>
                        <SelectItem value="DC">DC</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Zip">
                    <Input
                      value={form.zip_code}
                      readOnly
                      className="bg-slate-50 cursor-not-allowed"
                      data-testid="booking-zip"
                    />
                  </Field>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Appliance type *">
                  <Select
                    value={form.appliance_type}
                    onValueChange={(v) => setField("appliance_type", v)}
                  >
                    <SelectTrigger data-testid="booking-appliance-type">
                      <SelectValue placeholder="Select appliance" />
                    </SelectTrigger>
                    <SelectContent>
                      {APPLIANCE_TYPES.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Manufacturer *">
                  <Select
                    value={form.manufacturer}
                    onValueChange={(v) => setField("manufacturer", v)}
                  >
                    <SelectTrigger data-testid="booking-manufacturer">
                      <SelectValue placeholder="Select manufacturer" />
                    </SelectTrigger>
                    <SelectContent>
                      {MANUFACTURERS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Describe the problem *">
                <Textarea
                  rows={4}
                  value={form.problem_description}
                  onChange={(e) => setField("problem_description", e.target.value)}
                  placeholder="e.g. Refrigerator not cooling, freezer works fine. Started 2 days ago."
                  data-testid="booking-problem"
                />
              </Field>

              <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-400 text-blue-700 focus:ring-blue-600 cursor-pointer"
                    data-testid="sms-consent-checkbox"
                  />
                  <span className="text-sm text-slate-700 leading-relaxed" data-testid="sms-consent-label">
                    <strong>(Optional)</strong> I agree to receive SMS text messages from
                    Bell Appliance Repair regarding my service appointment (booking
                    confirmation, appointment reminders, and technician arrival
                    notifications). Message frequency varies (up to 4 messages per
                    appointment). Message and data rates may apply. Reply STOP to opt out
                    or HELP for help. <strong>Consent is not required to book</strong>{" "}
                    &mdash; if you leave this unchecked we&apos;ll contact you by phone or
                    email instead. See our{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:underline font-medium"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:underline font-medium"
                    >
                      Terms of Service
                    </a>
                    .
                  </span>
                </label>
              </div>

              <div className="rounded-md border border-amber-300 bg-amber-50 p-4" data-testid="service-fee-notice">
                <div className="font-semibold text-amber-900 mb-2">Service Fee Notice</div>
                <p className="text-sm text-amber-900/90 leading-relaxed">
                  A <strong>$100 service/diagnostic fee</strong> is due for each service visit. This fee covers the technician's travel to your home, diagnosis of the appliance, and a written repair estimate.
                </p>
                <p className="text-sm text-amber-900/90 leading-relaxed mt-2">
                  If you choose to have Bell Appliance Repair complete the repair, the $100 service fee is <strong>applied toward the repair cost</strong>.
                </p>
                <p className="text-sm text-amber-900/90 leading-relaxed mt-2">
                  If you decide not to proceed with the repair after receiving the estimate, only the $100 service fee is due. <strong>No repair work will begin without your approval.</strong>
                </p>
                <p className="text-xs text-amber-800 mt-3">
                  Payment is collected on-site at the time of service — cash or card.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-between">
                <Button variant="ghost" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                  disabled={!canSubmit || submitting}
                  onClick={submit}
                  className="bg-blue-700 hover:bg-blue-800"
                  data-testid="submit-booking"
                >
                  {submitting ? "Sending…" : "Request appointment"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && confirmation && (
          <Card className="border-slate-200 shadow-sm" data-testid="booking-confirmation">
            <CardContent className="py-10 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-3xl font-extrabold text-slate-900">
                Request received
              </h2>
              <p className="text-slate-600 max-w-md mx-auto">
                Thanks {form.first_name}! Your appointment request is pending confirmation.
                We'll email you to confirm, usually within a few hours.
              </p>
              <div className="inline-block bg-slate-50 border border-slate-200 rounded-md px-6 py-4 text-left">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                  Confirmation
                </div>
                <div className="font-mono font-bold text-lg text-slate-900" data-testid="confirmation-code">
                  #{confirmation.code}
                </div>
                <div className="mt-2 text-sm text-slate-700">
                  <div>{confirmation.date}</div>
                  <div>{confirmation.slot}</div>
                </div>
              </div>
              <div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep(1);
                    setSelectedDate(null);
                    setSelectedSlot(null);
                    setConfirmation(null);
                    setSmsConsent(false);
                    setZipQuery("");
                    setZipCheck(null);
                    setForm({
                      first_name: "",
                      last_name: "",
                      email: "",
                      phone: "",
                      street: "",
                      city: "",
                      state: "",
                      zip_code: "",
                      appliance_type: "",
                      manufacturer: "",
                      problem_description: "",
                    });
                  }}
                  data-testid="book-another"
                >
                  Book another appointment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-6 py-6 text-center text-xs text-slate-400 space-x-3">
        <span>Powered by FixTrack · Bell Appliance Repair</span>
        <span aria-hidden>·</span>
        <a href="/privacy" className="hover:underline">Privacy</a>
        <span aria-hidden>·</span>
        <a href="/terms" className="hover:underline">Terms</a>
      </footer>
    </div>
  );
}

function StepDot({ n, active, label }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
          active ? "bg-blue-700 text-white" : "bg-slate-200 text-slate-500"
        }`}
      >
        {n}
      </div>
      <div
        className={`text-sm ${active ? "text-slate-900 font-medium" : "text-slate-500"}`}
      >
        {label}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
